import { debounce } from 'lodash-es';
import { saveTempFileApi } from '@/api/chat';

class HtmlPreview extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._lastIframeHeight = 0;
    this.debouncedAdjustHeight = debounce(iframe => {
      this.adjustIframeHeight(iframe);
    }, 100);
  }

  async connectedCallback() {
    const code = this.getAttribute('code');
    const botId = this.getAttribute('bot-id');
    const botKey = this.getAttribute('bot-key');
    console.log(botKey, 89765);

    let iframeSrc = '';
    let blobUrl = '';
    if (botId && botKey) {
      const res = await saveTempFileApi({
        secret_id: botId,
        secret_key: botKey,
        data: decodeURIComponent(code)
      });
      iframeSrc = `/api/app/cybertron/get_temp_file/${res.file_name}`;
    } else {
      const decodedCode = decodeURIComponent(code);
      const blob = new Blob([decodedCode], { type: 'text/html' });
      blobUrl = URL.createObjectURL(blob);
      iframeSrc = blobUrl;
    }

    const iframe = document.createElement('iframe');
    iframe.style.width = `100%`;
    iframe.style.height = '200px';
    iframe.style.border = 'none';
    iframe.style.overflow = 'hidden';
    iframe.src = iframeSrc;

    iframe.onload = () => {
      this.injectNeutralizingStyle(iframe);
      this.adjustIframeHeight(iframe);
      this.setupHeightObserver(iframe);
    };

    this.shadowRoot.appendChild(iframe);
    this._blobUrl = blobUrl;
  }

  /**
   * Inject neutralizing styles once to override viewport-relative units (e.g. 100vh)
   * in user CSS. In an iframe embed scenario, 100vh is meaningless (not a full-screen page)
   * and causes a circular dependency between iframe height and content height.
   * Use !important to ensure priority.
   */
  injectNeutralizingStyle(iframe) {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const style = doc.createElement('style');
      style.textContent = `
        html, body {
          min-height: 0 !important;
          height: auto !important;
        }
      `;
      doc.head.appendChild(style);
    } catch (e) {
      // Cross-origin iframe cannot access contentDocument, silently ignore
    }
  }

  /**
   * Dynamically adjust iframe height:
   * 1. Content <= container height → iframe fits content height
   * 2. Content > container height → iframe fixed to container height, inner scroll
   *
   * Convergence guard: skip adjustment when height delta < 2px to avoid ResizeObserver micro-jitter loop
   */
  adjustIframeHeight(iframe) {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const contentHeight = Math.max(
        doc.body.scrollHeight,
        doc.documentElement.scrollHeight
      );

      const maxHeight = this.clientHeight;
      const targetHeight = (maxHeight > 0 && contentHeight > maxHeight)
        ? maxHeight
        : contentHeight + 20;

      // Convergence guard: skip when height delta < 2px to break ResizeObserver micro-jitter loop
      if (Math.abs(targetHeight - this._lastIframeHeight) < 2) return;
      this._lastIframeHeight = targetHeight;

      if (maxHeight > 0 && contentHeight > maxHeight) {
        iframe.style.height = `${maxHeight}px`;
        iframe.style.overflow = 'auto';
      } else {
        iframe.style.height = `${contentHeight + 20}px`;
        iframe.style.overflow = 'hidden';
      }
    } catch (error) {
      // Cross-origin iframe cannot access contentDocument, silently ignore
    }
  }

  setupHeightObserver(iframe) {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          this.debouncedAdjustHeight(iframe);
        });

        resizeObserver.observe(doc.body);

        this._resizeObserver = resizeObserver;
      }
    } catch (error) {
      console.warn(error);
    }
  }

  disconnectedCallback() {
    if (this._blobUrl) {
      URL.revokeObjectURL(this._blobUrl);
      this._blobUrl = null;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this.debouncedAdjustHeight) {
      this.debouncedAdjustHeight.cancel();
    }
  }
}

customElements.define('html-preview', HtmlPreview);
